import type { PushConfigDto, PushSubscriptionDto } from '@fos/shared/contracts/notifications';
import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { toast } from '../../../ui/Toast';
import {
  currentBrowserSubscription,
  deviceLabelFromUserAgent,
  endpointFingerprint,
  notificationPermission,
  pushSupport,
  requestNotificationPermission,
  serviceWorkerRegistration,
  subscribeBrowser,
  unsubscribeBrowser,
} from './push-browser';

/**
 * Stan powiadomień na TYM urządzeniu: przeglądarka (wsparcie, zgoda, service worker, subskrypcja) + serwer (czy zna
 * subskrypcję — po odcisku adresu). Włączenie: zgoda → `pushManager.subscribe` z kluczem VAPID → zapis na serwerze.
 * Wyłączenie: usunięcie na serwerze i wyrejestrowanie w przeglądarce.
 */
export type PushDeviceState =
  | 'checking'
  | 'unsupported'
  | 'ios_needs_install'
  | 'no_service_worker'
  | 'denied'
  | 'off'
  | 'on';

export interface PushDevice {
  state: PushDeviceState;
  /** odcisk adresu subskrypcji tej przeglądarki (oznaczenie „To urządzenie” na liście) */
  fingerprint: string | null;
  label: string;
  busy: boolean;
  enable(): Promise<void>;
  disable(): Promise<void>;
}

export const BROWSER_SUBSCRIBE_HINT =
  'Sprawdź, czy to nie okno prywatne (incognito) i czy powiadomienia dla tej strony są dozwolone, potem spróbuj ponownie.';

export function usePushDevice(
  config: PushConfigDto | undefined,
  devices: PushSubscriptionDto[] | undefined,
): PushDevice {
  const utils = trpc.useUtils();
  const subscribe = trpc.notifications.subscribe.useMutation();
  const unsubscribe = trpc.notifications.unsubscribe.useMutation();
  const [browser, setBrowser] = useState<{ state: PushDeviceState; fingerprint: string | null }>({
    state: 'checking',
    fingerprint: null,
  });
  const [busy, setBusy] = useState(false);
  const [label] = useState(() =>
    typeof navigator === 'undefined' ? 'To urządzenie' : deviceLabelFromUserAgent(navigator.userAgent),
  );

  const check = useCallback(async () => {
    const support = pushSupport();
    if (support !== 'supported') return setBrowser({ state: support, fingerprint: null });
    if (notificationPermission() === 'denied') return setBrowser({ state: 'denied', fingerprint: null });
    try {
      if (!(await serviceWorkerRegistration())) return setBrowser({ state: 'no_service_worker', fingerprint: null });
      const sub = await currentBrowserSubscription();
      const fingerprint = sub ? await endpointFingerprint(sub.endpoint) : null;
      setBrowser({ state: fingerprint ? 'on' : 'off', fingerprint });
    } catch {
      setBrowser({ state: 'off', fingerprint: null });
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // „on” tylko, gdy serwer zna subskrypcję tej przeglądarki (np. usunięta z innego urządzenia = „off”)
  const known = Boolean(browser.fingerprint && devices?.some((d) => d.fingerprint === browser.fingerprint));
  const state: PushDeviceState = browser.state === 'on' && devices !== undefined && !known ? 'off' : browser.state;

  const enable = useCallback(async () => {
    if (!config?.available || !config.publicKey) return;
    setBusy(true);
    try {
      const permission = await requestNotificationPermission();
      if (permission !== 'granted') {
        setBrowser({ state: permission === 'denied' ? 'denied' : 'off', fingerprint: null });
        if (permission !== 'denied') toast.info('Powiadomienia nie zostały włączone');
        return;
      }
      let sub: Awaited<ReturnType<typeof subscribeBrowser>>;
      try {
        sub = await subscribeBrowser(config.publicKey);
      } catch {
        // błąd przeglądarki (DOMException po angielsku, np. okno incognito w Chrome): komunikat po polsku
        toast.error('Przeglądarka nie włączyła powiadomień', { description: BROWSER_SUBSCRIBE_HINT });
        return;
      }
      await subscribe.mutateAsync({ endpoint: sub.endpoint, keys: sub.keys, deviceLabel: label });
      setBrowser({ state: 'on', fingerprint: await endpointFingerprint(sub.endpoint) });
      await utils.notifications.devices.invalidate();
      toast.success('Powiadomienia włączone na tym urządzeniu');
    } catch (err) {
      toast.error('Nie udało się włączyć powiadomień', { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }, [config, label, subscribe, utils]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const mine = devices?.find((d) => d.fingerprint === browser.fingerprint);
      if (mine) await unsubscribe.mutateAsync({ id: mine.id });
      // serwer już nie wyśle; błąd wyrejestrowania w przeglądarce nie zmienia wyniku
      await unsubscribeBrowser().catch(() => {});
      setBrowser({ state: 'off', fingerprint: null });
      await utils.notifications.devices.invalidate();
      toast.success('Powiadomienia wyłączone na tym urządzeniu');
    } catch (err) {
      toast.error('Nie udało się wyłączyć powiadomień', { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }, [browser.fingerprint, devices, unsubscribe, utils]);

  return { state, fingerprint: known ? browser.fingerprint : null, label, busy, enable, disable };
}
